import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Handshake, TrendingUp, Users, Link2, Ticket, DollarSign, Calendar,
  BadgeCheck, Sparkles, ArrowRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { useLocale } from "@/i18n/useLocale";
import content from "@/data/partner_public_content.json";

// Icons ride on top of the content (icons don't translate).
const STEP_ICONS = [Handshake, Link2, Users, DollarSign];
const CALLOUT_ICONS = [Link2, DollarSign, Calendar, TrendingUp];
const MODEL_ICONS = [Calendar, Calendar, Ticket];

// Tier ranges are locale-agnostic (Western digits). Rate/labels resolve via content.
const TIER_ROWS = [
  { key: "Starter", range: "1–20", rate: "15%" },
  { key: "Growth", range: "21–40", rate: "20%" },
  { key: "Scale", range: "41–100", rate: "25%" },
  { key: "Elite", range: "101+", rate: "30%" },
];

export default function PartnerProgramPublic() {
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const { locale, isRtl } = useLocale();
  const isAr = String(locale || "en").toLowerCase().startsWith("ar");
  const c = (isAr ? content.ar : content.en).public;

  useSeo({
    title: c.seoTitle,
    description: c.seoDescription,
    path: "/partners",
  });

  const applyHref = "/partners/apply";

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="aria-dark relative min-h-screen overflow-hidden bg-[#050607] text-white"
      style={{ fontFamily: "'Outfit', sans-serif" }}
      data-testid="partners-public-page"
    >
      <div className="grain-overlay" style={{ opacity: 0.05 }} />
      <div className="aria-gold-radial pointer-events-none absolute inset-0 opacity-60" />

      {/* Sticky mini-nav */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#050607]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[64px] max-w-[1320px] items-center justify-between px-5 sm:px-8 lg:px-12">
          <Link to="/" className="flex items-center gap-2.5" data-testid="partners-brand">
            <img src="/tp-mark.png" alt="TapPresence" className="h-8 w-8 object-contain" />
            <span className="text-[15px] font-semibold tracking-tight">TapPresence</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-white/60 hover:text-white" data-testid="partners-home">{c.nav.home}</Link>
            {user ? (
              <Link to="/dashboard" className="text-sm text-white/60 hover:text-white" data-testid="partners-dashboard">{c.nav.dashboard}</Link>
            ) : (
              <Link to="/login?intent=partner&return_to=/partner/dashboard" className="text-sm text-white/60 hover:text-white" data-testid="partners-signin">{c.nav.signIn}</Link>
            )}
            <button
              onClick={() => nav(applyHref)}
              data-testid="partners-hero-cta"
              className="rounded-xl bg-[#D6A653] px-4 py-2 text-sm font-medium text-black hover:bg-[#c99a49]"
            >
              {c.nav.becomePartner}
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="mx-auto max-w-[1320px] px-5 pt-16 sm:px-8 lg:px-12 lg:pt-24">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#D6A653]/30 bg-[#0D1014] px-3.5 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-[#F0CD84]" />
            <span className="text-[12px] text-[#E6C787]">{c.hero.badge}</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-6 font-semibold tracking-[-0.03em] text-white"
            style={{ fontSize: "clamp(38px, 5.6vw, 62px)", lineHeight: 1.05 }}
            data-testid="partners-hero-title">
            {c.hero.titleLine1}<br />
            {c.hero.titleLine2Prefix}<span className="lp-shine">{c.hero.titleLine2Product}</span>
          </motion.h1>
          <p className="mt-6 max-w-[640px] text-[17px] leading-relaxed text-white/70" data-testid="partners-hero-sub">
            {c.hero.sub}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              onClick={() => nav(applyHref)}
              data-testid="partners-hero-apply"
              className="inline-flex items-center gap-2 rounded-xl bg-[#D6A653] px-6 py-3.5 text-[15px] font-medium text-black hover:bg-[#c99a49]"
            >
              {c.hero.ctaApply} <ArrowRight className={`h-4 w-4 ${isRtl ? "rotate-180" : ""}`} />
            </button>
            {!user && (
              <Link
                to="/login?return_to=/partner/dashboard&intent=partner"
                data-testid="partners-public-signin-button"
                className="inline-flex items-center gap-2 rounded-xl border border-[#D6A653]/60 bg-transparent px-6 py-3.5 text-[15px] font-medium text-[#D6A653] hover:bg-[#D6A653]/10 hover:text-[#F0CD84]"
              >
                {c.hero.ctaLogin}
              </Link>
            )}
            <a href="#how-it-works" className="text-[15px] text-white/70 underline underline-offset-4 hover:text-white" data-testid="partners-hero-learn">
              {c.hero.howLink}
            </a>
          </div>
          <p className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-white/50">
            {c.hero.badges.map((b, i) => (
              <span key={i} className="inline-flex items-center gap-1.5"><BadgeCheck className="h-4 w-4 text-[#D6A653]" /> {b}</span>
            ))}
          </p>
        </div>
      </section>

      {/* TIER LADDER */}
      <section className="mx-auto max-w-[1320px] px-5 py-16 sm:px-8 lg:px-12">
        <h2 className="text-[13px] font-semibold uppercase tracking-wider text-white/45">{c.tiers.kicker}</h2>
        <p className="mt-2 text-[24px] font-medium tracking-tight text-white">{c.tiers.title}</p>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="tier-ladder">
          {TIER_ROWS.map((t) => (
            <div key={t.key}
                 className="rounded-2xl border border-white/10 bg-[#0B0D12] p-6"
                 data-testid={`tier-card-${t.rate}`}>
              <p className="text-[11px] uppercase tracking-wider text-white/40">{c.tiers.labels[t.key]}</p>
              <p className="mt-3 text-4xl font-light text-[#D6A653]">{t.rate}</p>
              <p className="mt-1 text-sm text-white/60">{c.tiers.onPaidInvoices}</p>
              <p className="mt-4 border-t border-white/10 pt-4 text-xs text-white/50">
                {t.range} {c.tiers.seatsAttributed}
              </p>
            </div>
          ))}
        </div>
        <p className="mt-6 max-w-[720px] text-[13px] leading-relaxed text-white/50">
          {c.tiers.footnote}
        </p>
      </section>

      {/* SELL WHO */}
      <section className="mx-auto max-w-[1320px] px-5 py-8 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2" data-testid="who-you-sell-to">
          <div className="rounded-2xl border border-white/10 bg-[#0B0D12] p-6">
            <Users className="h-5 w-5 text-[#D6A653]" />
            <p className="mt-4 text-lg font-medium text-white">{c.sellWho.individuals.title}</p>
            <p className="mt-2 text-sm leading-relaxed text-white/60">{c.sellWho.individuals.body}</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0B0D12] p-6">
            <TrendingUp className="h-5 w-5 text-[#D6A653]" />
            <p className="mt-4 text-lg font-medium text-white">{c.sellWho.teams.title}</p>
            <p className="mt-2 text-sm leading-relaxed text-white/60">{c.sellWho.teams.body}</p>
          </div>
        </div>
      </section>

      {/* COMMISSION MODEL */}
      <section className="mx-auto max-w-[1320px] px-5 py-16 sm:px-8 lg:px-12">
        <h2 className="text-[13px] font-semibold uppercase tracking-wider text-white/45">{c.commissionModel.kicker}</h2>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="commission-model">
          {c.commissionModel.cards.map((card, i) => {
            const Icon = MODEL_ICONS[i] || Calendar;
            return <ModelCard key={i} icon={Icon} title={card.title} body={card.body} />;
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="mx-auto max-w-[1320px] px-5 py-16 sm:px-8 lg:px-12">
        <h2 className="text-[13px] font-semibold uppercase tracking-wider text-white/45">{c.howItWorks.kicker}</h2>
        <p className="mt-2 text-[24px] font-medium tracking-tight text-white">{c.howItWorks.title}</p>
        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="how-it-works">
          {c.howItWorks.steps.map((s, i) => {
            const Icon = STEP_ICONS[i] || Handshake;
            const n = i + 1;
            return (
              <div key={n} className="rounded-2xl border border-white/10 bg-[#0B0D12] p-6" data-testid={`step-${n}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#D6A653]/40 bg-[#D6A653]/10 text-xs text-[#D6A653]">{n}</span>
                  <Icon className="h-4 w-4 text-white/60" />
                </div>
                <p className="mt-4 text-sm font-medium text-white">{s.title}</p>
                <p className="mt-2 text-xs leading-relaxed text-white/55">{s.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FEATURE CALLOUTS */}
      <section className="mx-auto max-w-[1320px] px-5 py-8 sm:px-8 lg:px-12">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="feature-callouts">
          {c.featureCallouts.map((title, i) => (
            <Callout key={i} icon={CALLOUT_ICONS[i] || Link2} title={title} />
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-[880px] px-5 py-16 sm:px-8" data-testid="partners-faq">
        <h2 className="text-[13px] font-semibold uppercase tracking-wider text-white/45">{c.faq.kicker}</h2>
        <p className="mt-2 text-[24px] font-medium tracking-tight text-white">{c.faq.title}</p>
        <div className="mt-8 space-y-3">
          {c.faq.items.map((qa, i) => (
            <details key={i} className="group rounded-2xl border border-white/10 bg-[#0B0D12] p-5 open:bg-[#0F1218]" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer list-none text-sm font-medium text-white group-open:text-[#D6A653]">
                {qa.q}
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-white/65">
                {qa.a}{" "}
                {qa.link ? (
                  <Link to={qa.link} className="text-[#D6A653] underline underline-offset-2" data-testid={`faq-${i}-link`}>
                    {c.faq.readFullTerms}
                  </Link>
                ) : null}
              </p>
            </details>
          ))}
        </div>
        <p className="mt-8 text-xs text-white/40">
          {c.faq.footer
            .split("{email}").join("__EMAIL__")
            .split("{termsLink}").join("__TERMS__")
            .split("__EMAIL__").map((part, idx, arr) => (
              <span key={`e${idx}`}>
                {part}
                {idx < arr.length - 1 && (
                  <a href="mailto:partners@tappresence.com" className="text-[#D6A653] underline underline-offset-2" data-testid="partners-support-email">partners@tappresence.com</a>
                )}
              </span>
            ))}
        </p>
        {/* Second render for the terms link — kept simple: use a separate line to avoid nested splits */}
        <p className="text-xs text-white/40 sr-only">
          <Link to="/legal/partner-terms" className="text-[#D6A653] underline underline-offset-2" data-testid="partners-terms-link">{c.faq.termsLinkLabel}</Link>
        </p>
      </section>

      {/* SECOND CTA */}
      <section className="mx-auto max-w-[1320px] px-5 pb-24 sm:px-8 lg:px-12">
        <div className="rounded-3xl border border-[#D6A653]/30 bg-gradient-to-br from-[#0F1218] to-[#050607] p-10 sm:p-14 text-center" data-testid="partners-second-cta">
          <h2 className="text-3xl font-medium tracking-tight text-white">{c.secondCta.title}</h2>
          <p className="mx-auto mt-3 max-w-[560px] text-sm text-white/60">{c.secondCta.sub}</p>
          <button
            onClick={() => nav(applyHref)}
            data-testid="partners-second-cta-btn"
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-[#D6A653] px-8 py-4 text-[15px] font-medium text-black hover:bg-[#c99a49]"
          >
            {c.secondCta.cta} <ArrowRight className={`h-4 w-4 ${isRtl ? "rotate-180" : ""}`} />
          </button>
        </div>
      </section>

      <footer className="border-t border-white/8 py-8 text-center text-xs text-white/40">
        <p>
          © {new Date().getFullYear()} TapPresence ·{" "}
          <Link to="/legal/partner-terms" className="underline underline-offset-2 hover:text-white">{c.footer.terms}</Link>
          {" · "}
          <Link to="/legal/privacy" className="underline underline-offset-2 hover:text-white">{c.footer.privacy}</Link>
          {" · "}
          <Link to="/legal/terms" className="underline underline-offset-2 hover:text-white">{c.footer.generalTerms}</Link>
        </p>
      </footer>
    </div>
  );
}

const ModelCard = ({ icon: Icon, title, body }) => (
  <div className="rounded-2xl border border-white/10 bg-[#0B0D12] p-6">
    <Icon className="h-5 w-5 text-[#D6A653]" />
    <p className="mt-4 text-lg font-medium text-white">{title}</p>
    <p className="mt-2 text-sm leading-relaxed text-white/60">{body}</p>
  </div>
);

const Callout = ({ icon: Icon, title }) => (
  <div className="rounded-2xl border border-white/10 bg-[#0B0D12] p-4">
    <Icon className="h-4 w-4 text-[#D6A653]" />
    <p className="mt-3 text-xs font-medium text-white">{title}</p>
  </div>
);
